import { useState } from "react";
import type { Lead } from "./LeadModal";

/* ─── Types ──────────────────────────────────────────────────── */
export type ActionMode = "convert" | "job" | "contract";

interface Props {
  lead: Lead;
  mode: ActionMode;
  onClose: () => void;
  onConvert:       (opts: ConvertOpts)  => Promise<void>;
  onCreateJob:     (opts: JobOpts)      => Promise<void>;
  onCreateContract:(opts: ContractOpts) => Promise<void>;
}

export interface ConvertOpts {
  skipIfExists: boolean;
}

export interface JobOpts {
  title:      string;
  job_type:   string;
  shoot_date: string;
  location:   string;
  notes:      string;
}

export interface ContractOpts {
  title:          string;
  event_location: string;
  package_name:   string;
  total_amount:   number;
  deposit_amount: number;
}

const JOB_TYPES = [
  "Wedding", "Portrait", "Engagement", "Family", "Newborn",
  "Event", "Corporate", "Product", "Real Estate", "Boudoir", "Other",
];

const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all";

/* ─── Component ──────────────────────────────────────────────── */
export default function LeadActionsModal({ lead, mode, onClose, onConvert, onCreateJob, onCreateContract }: Props) {
  const [saving, setSaving] = useState(false);

  /* ── Convert form ── */
  const [skipIfExists, setSkipIfExists] = useState(true);

  /* ── Job form ── */
  const [jobTitle,    setJobTitle]    = useState(lead.event_type ? `${lead.event_type} — ${lead.name}` : lead.name);
  const [jobType,     setJobType]     = useState(lead.event_type ?? "Wedding");
  const [jobDate,     setJobDate]     = useState(lead.event_date ?? "");
  const [jobLocation, setJobLocation] = useState("");
  const [jobNotes,    setJobNotes]    = useState(lead.message ?? "");

  /* ── Contract form ── */
  const [ctTitle,    setCtTitle]    = useState(lead.event_type ? `${lead.event_type} — ${lead.name}` : lead.name);
  const [ctLocation, setCtLocation] = useState("");
  const [ctPackage,  setCtPackage]  = useState("");
  const [ctTotal,    setCtTotal]    = useState("");
  const [ctDeposit,  setCtDeposit]  = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === "convert") {
        await onConvert({ skipIfExists });
      } else if (mode === "job") {
        await onCreateJob({
          title:      jobTitle.trim(),
          job_type:   jobType,
          shoot_date: jobDate,
          location:   jobLocation.trim(),
          notes:      jobNotes.trim(),
        });
      } else {
        await onCreateContract({
          title:          ctTitle.trim(),
          event_location: ctLocation.trim(),
          package_name:   ctPackage.trim(),
          total_amount:   parseFloat(ctTotal)   || 0,
          deposit_amount: parseFloat(ctDeposit) || 0,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const TITLES: Record<ActionMode, { icon: string; label: string; sub: string; accent: string }> = {
    convert:  { icon: "ri-user-add-line",  label: "Convert to Client",    sub: "Add this lead to your Clients list",   accent: "bg-[#C8A96E]/10 text-[#9a7840]" },
    job:      { icon: "ri-camera-3-line",  label: "Create Job from Lead", sub: "Schedule a shoot for this lead",        accent: "bg-violet-50 text-violet-600" },
    contract: { icon: "ri-file-text-line", label: "Create Contract",      sub: "Draft a contract for this lead",        accent: "bg-emerald-50 text-emerald-600" },
  };
  const { icon, label, sub, accent } = TITLES[mode];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-slate-200 w-full max-w-md shadow-xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${accent}`}>
              <i className={`${icon} text-base`}></i>
            </div>
            <div>
              <h2 className="text-slate-800 font-semibold text-sm">{label}</h2>
              <p className="text-slate-400 text-xs mt-0.5">{sub}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Lead summary strip */}
        <div className="mx-6 mt-4 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C8A96E]/15 text-[#9a7840] text-sm font-semibold shrink-0">
              {lead.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-slate-800 text-sm font-medium truncate">{lead.name}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {lead.email && <span className="text-slate-400 text-xs truncate">{lead.email}</span>}
                {lead.event_type && <span className="text-slate-300 text-xs">· {lead.event_type}</span>}
                {lead.event_date && (
                  <span className="text-slate-300 text-xs">
                    · {new Date(lead.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">

          {/* ── CONVERT ── */}
          {mode === "convert" && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <p className="text-emerald-700 text-sm font-medium mb-1">Ready to convert</p>
                <p className="text-emerald-600/70 text-xs leading-relaxed">
                  This will create a new client record with the lead&apos;s name, email, phone, and event notes.
                  The lead will be marked as <strong>Booked</strong>.
                </p>
              </div>

              {/* Duplicate check option */}
              <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition-colors">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={skipIfExists}
                    onChange={(e) => setSkipIfExists(e.target.checked)}
                    className="w-4 h-4 rounded accent-[#C8A96E] cursor-pointer"
                  />
                </div>
                <div>
                  <p className="text-slate-700 text-sm font-medium group-hover:text-slate-800 transition-colors">
                    Skip if client already exists
                  </p>
                  <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">
                    Checks for matching email or phone before creating. Prevents duplicates.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* ── CREATE JOB ── */}
          {mode === "job" && (
            <div className="space-y-3">
              <div>
                <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
                  Job Title <span className="text-red-400">*</span>
                </label>
                <input
                  required
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className={INPUT}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Job Type</label>
                  <select
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                    className={`${INPUT} cursor-pointer`}
                  >
                    {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Shoot Date</label>
                  <input
                    type="date"
                    value={jobDate}
                    onChange={(e) => setJobDate(e.target.value)}
                    className={`${INPUT} cursor-pointer`}
                  />
                </div>
              </div>
              <div>
                <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Location</label>
                <input
                  value={jobLocation}
                  onChange={(e) => setJobLocation(e.target.value)}
                  className={INPUT}
                  placeholder="Venue or address"
                />
              </div>
              <div>
                <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Notes</label>
                <textarea
                  value={jobNotes}
                  onChange={(e) => setJobNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className={`${INPUT} resize-none`}
                  placeholder="Additional notes..."
                />
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-amber-700 text-xs leading-relaxed">
                  <i className="ri-information-line mr-1"></i>
                  The lead will be converted to a client first, then the job will be linked. Lead marked as Booked.
                </p>
              </div>
            </div>
          )}

          {/* ── CREATE CONTRACT ── */}
          {mode === "contract" && (
            <div className="space-y-3">
              <div>
                <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
                  Contract Title <span className="text-red-400">*</span>
                </label>
                <input
                  required
                  value={ctTitle}
                  onChange={(e) => setCtTitle(e.target.value)}
                  className={INPUT}
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Location</label>
                  <input
                    value={ctLocation}
                    onChange={(e) => setCtLocation(e.target.value)}
                    className={INPUT}
                    placeholder="Venue name"
                  />
                </div>
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Package Name</label>
                  <input
                    value={ctPackage}
                    onChange={(e) => setCtPackage(e.target.value)}
                    className={INPUT}
                    placeholder="e.g. Full Day Coverage"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Total Amount ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ctTotal}
                      onChange={(e) => setCtTotal(e.target.value)}
                      className={`${INPUT} pl-7`}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Deposit ($)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ctDeposit}
                      onChange={(e) => setCtDeposit(e.target.value)}
                      className={`${INPUT} pl-7`}
                      placeholder="0.00"
                    />
                  </div>
                  {ctTotal && ctDeposit && parseFloat(ctDeposit) > parseFloat(ctTotal) && (
                    <p className="text-red-400 text-xs mt-1">Deposit exceeds total</p>
                  )}
                </div>
              </div>
              {ctTotal && ctDeposit && parseFloat(ctDeposit) <= parseFloat(ctTotal) && (
                <div className="px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-slate-500 text-xs">
                    Balance: <strong className="text-slate-700">
                      ${(parseFloat(ctTotal) - parseFloat(ctDeposit)).toFixed(2)}
                    </strong>
                  </p>
                </div>
              )}
              <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-amber-700 text-xs leading-relaxed">
                  <i className="ri-information-line mr-1"></i>
                  The lead will be converted to a client first, then the contract will be created as a draft.
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (mode === "contract" && !!ctTotal && !!ctDeposit && parseFloat(ctDeposit) > parseFloat(ctTotal))}
              className="flex items-center gap-2 px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <i className={icon}></i>
                  {mode === "convert" ? "Convert to Client" : mode === "job" ? "Create Job" : "Create Contract"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}